import { AxiosError } from 'axios';

/**
 * What a `study/:id/*-items` request asks for, as `status` in its body: `ItemStatus` in spdata-api's
 * `study.item.status.service.ts`. `active` is a restore. `delete-items` always deletes.
 */
export type ItemStatus = 'locked' | 'unlocked' | 'archive' | 'active' | 'delete';

/**
 * Why spdata-api refused a lock, unlock, archive, restore or delete of one item: `RefusalReason` in
 * its `study.item.status.service.ts` (ENG-9034).
 *
 * - `not-found`: no live item with this id in the study (404).
 * - `not-permitted`: the caller lacks the permission this change needs on this item (403).
 * - `locked`: an archive, restore or delete of a locked item (409).
 * - `case-locked`: an entry whose case is locked, for anything but a lock (409).
 */
export const ITEM_REFUSAL_REASONS = ['not-found', 'not-permitted', 'locked', 'case-locked'] as const;

export type ItemRefusalReason = (typeof ITEM_REFUSAL_REASONS)[number];

/** Each reason's copy in `locales/`, which a refused row draws beside itself. */
export const ITEM_REFUSAL_REASON_KEYS: Record<ItemRefusalReason, string> = {
  'not-found': 'item_refusal_reason_not_found',
  'not-permitted': 'item_refusal_reason_not_permitted',
  locked: 'item_refusal_reason_locked',
  'case-locked': 'item_refusal_reason_case_locked'
};

export interface RefusedItem {
  /** Lowercase hex. The server lowercases the id the client sent, so compare in lowercase. */
  id: string;
  type: 'case' | 'entry';
  reason: ItemRefusalReason;
}

const isRefusedItem = (value: unknown): value is RefusedItem => {
  const item = value as Partial<RefusedItem> | null;
  return typeof item?.id === 'string' &&
    (item.type === 'case' || item.type === 'entry') &&
    (ITEM_REFUSAL_REASONS as readonly unknown[]).includes(item.reason);
};

/**
 * The items a `study/:id/*-items` refusal names, or `undefined` for a failure that names none: a 400,
 * a 500, the network. The routes are all or nothing, so a refusal means nothing was changed.
 *
 * An item with a reason this client doesn't know is left out rather than given one.
 */
export const refusedItemsOf = (error: unknown): RefusedItem[] | undefined => {
  const refused: unknown = error instanceof AxiosError ? error.response?.data?.refused : undefined;
  if (!Array.isArray(refused)) {
    return undefined;
  }
  const items = refused.filter(isRefusedItem).map(item => ({ ...item, id: item.id.toLowerCase() }));
  return items.length > 0 ? items : undefined;
};
